/** Legal copy adapted from mosida.com Terms of Use & Privacy Policy. */

export const TERMS_OF_USE = {
  title: 'MOSIDA TERMS OF USE',
  intro: [
    'PLEASE READ THESE TERMS OF USE CAREFULLY.',
    'If you disagree with any part of these Terms, please refrain from using our Site, facilities, or services. MOSIDA may update these Terms at any time and may condition continued use upon acceptance of revised Terms.',
    'The terms “MOSIDA,” “we,” “us,” or “our” refer to MOSIDA LLC, a Florida limited liability company, and its affiliates.',
    'The term “you” refers to any visitor, ticket purchaser, website user, or participant.',
    'By accessing or using mosida.com, any MOSIDA mobile application, interactive system, AI station, onsite technology, ticketing platform, or physical exhibition space (collectively, the “Site” or “Experience”), you agree to comply with these Terms.',
  ],
  sections: [
    {
      heading: '1. The Site & Experience',
      blocks: [
        {
          sub: '1.1 Eligibility',
          paragraphs: [
            'You must be at least 16 years old to use MOSIDA’s online services independently. Visitors under 16 must be accompanied by a parent or legal guardian.',
            'Parents or guardians assume full responsibility for minors in their care, including participation in interactive exhibits and digital content creation.',
          ],
        },
        {
          sub: '1.2 Permission to Use',
          paragraphs: [
            'MOSIDA grants you a limited, non-exclusive, revocable license to access and use the Site and Experience subject to these Terms. Your use is at your own risk.',
          ],
        },
        {
          sub: '1.3 Availability',
          paragraphs: [
            'MOSIDA reserves the right to modify, suspend, update, or discontinue any portion of the Site, Experience, or exhibits at any time without notice.',
          ],
        },
      ],
    },
    {
      heading: '2. Privacy & Communications',
      blocks: [
        {
          sub: '2.1 Privacy Policy',
          paragraphs: [
            'Your use of MOSIDA is governed by our Privacy Policy, incorporated herein by reference.',
          ],
        },
        {
          sub: '2.2 User Information',
          paragraphs: [
            'You agree to provide accurate and complete information when purchasing tickets, creating accounts, submitting content, or using AI systems.',
            'You may not impersonate another individual or provide false identity information.',
          ],
        },
        {
          sub: '2.3 Communications',
          paragraphs: [
            'By purchasing tickets, submitting information, or participating in MOSIDA programs, you consent to receive communications via email, SMS, or other methods related to ticket confirmations, event updates, promotions, special exhibitions, and administrative notices.',
            'You may unsubscribe from marketing communications at any time.',
          ],
        },
      ],
    },
    {
      heading: '3. Interactive Content & AI-Generated Works',
      blocks: [
        {
          sub: '3.1 Definitions',
          paragraphs: [
            '“Content” includes text, images, video, audio, biometric data, motion data, AI outputs, digital art, projections, and all other media.',
            '“Your Content” means content you submit, upload, speak, photograph, record, or create through MOSIDA systems.',
            '“MOSIDA Content” means all content created or owned by MOSIDA, including AI systems, projections, software, exhibits, digital environments, and artwork.',
          ],
        },
        {
          sub: '3.2 Ownership of Your Content',
          paragraphs: [
            'You retain ownership of original content you submit. However, by participating in MOSIDA experiences, you grant MOSIDA a worldwide, perpetual, royalty-free, irrevocable, transferable, sublicensable license to use, reproduce, modify, transform into AI-generated works, display publicly, distribute, create derivative works, and use in advertising, promotion, and future exhibitions.',
            'You waive any moral rights or claims of attribution.',
          ],
        },
        {
          sub: '3.3 AI-Generated Content',
          paragraphs: [
            'MOSIDA utilizes artificial intelligence systems to generate artworks incorporating visitor input. You acknowledge that AI outputs may transform your likeness, may include your image with other visitors, are artistic interpretations, and that MOSIDA retains ownership of AI-generated outputs created within its systems.',
          ],
        },
        {
          sub: '3.4 Photography, Recording & Likeness Release',
          paragraphs: [
            'By entering MOSIDA, you consent to being photographed, recorded, scanned, or digitally captured, and grant MOSIDA the right to use your name, image, likeness, voice, and digital representation for promotional and exhibition purposes.',
            'If you do not wish to appear in promotional materials, you must notify staff in writing prior to participation.',
          ],
        },
        {
          sub: '3.5 MOSIDA Intellectual Property',
          paragraphs: [
            'All MOSIDA systems, code, software, installations, visual interfaces, interactive mechanics, and brand elements — including the slogan “The Masterpiece Starts With You.” — are protected by intellectual property laws. You may not copy installations, reverse engineer software, commercially distribute full exhibits, or use MOSIDA trademarks without written consent.',
          ],
        },
      ],
    },
    {
      heading: '4. Prohibited Conduct',
      blocks: [
        {
          paragraphs: [
            'You agree not to damage installations, interfere with interactive systems, upload unlawful content, harass staff or visitors, use MOSIDA systems for unauthorized commercial purposes, or attempt to hack or exploit AI systems. Violation may result in removal without refund.',
          ],
        },
      ],
    },
    {
      heading: '5. Health & Safety; Assumption of Risk',
      blocks: [
        {
          paragraphs: [
            'MOSIDA is an immersive, technology-driven environment that may include low lighting, moving projections, stairs or uneven flooring, sound effects, visual stimulation, and motion tracking.',
            'By entering, you acknowledge and voluntarily assume all risks associated with slips, falls, injury, sensory discomfort, and equipment malfunction, and release MOSIDA from liability except in cases of gross negligence or willful misconduct.',
          ],
        },
      ],
    },
    {
      heading: '6. Ticket Purchase Terms',
      blocks: [
        {
          paragraphs: [
            'All ticket sales are final unless otherwise required by law. By purchasing or redeeming a ticket, you agree to assume all risks of participation for yourself and minors in your care. Failure to comply with MOSIDA’s Code of Conduct may result in removal without refund.',
          ],
        },
      ],
    },
    {
      heading: '7. Warranty Disclaimer',
      blocks: [
        {
          paragraphs: [
            'THE SITE AND EXPERIENCE ARE PROVIDED “AS IS” AND “AS AVAILABLE.” MOSIDA MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.',
          ],
        },
      ],
    },
    {
      heading: '8. Limitation of Liability',
      blocks: [
        {
          paragraphs: [
            'To the maximum extent permitted by law, MOSIDA shall not be liable for indirect, incidental, special, or consequential damages. If liability cannot be excluded, MOSIDA’s maximum liability shall not exceed the amount paid for your ticket.',
          ],
        },
      ],
    },
    {
      heading: '9. Dispute Resolution',
      blocks: [
        {
          paragraphs: [
            'These Terms are governed by the laws of the State of Florida. All disputes shall be resolved by binding arbitration in Miami-Dade County, Florida, under the Federal Arbitration Act. Class actions are waived. Claims must be brought within one (1) year.',
          ],
        },
      ],
    },
    {
      heading: '10. Miscellaneous',
      blocks: [
        {
          paragraphs: [
            'These Terms constitute the entire agreement. Failure to enforce a provision is not a waiver. If a provision is invalid, the remainder remains enforceable. MOSIDA may assign these Terms without restriction.',
          ],
        },
      ],
    },
  ],
};

export const PRIVACY_POLICY = {
  title: 'MOSIDA PRIVACY POLICY',
  intro: [
    'Effective Date: February 19, 2026',
    'At MOSIDA (Museum of Social Interactive Digital Art), we take privacy seriously. This Privacy Policy is part of our Terms of Use and explains how we collect, use, and share information about you in connection with your use of our websites, emails, ticketing services, onsite interactive exhibits, kiosks, and any mobile applications (collectively, the “Service”).',
    'The terms “MOSIDA,” “we,” “us,” or “our” refer to MOSIDA LLC (Florida) and its affiliates. When you use the Service, you consent to the collection, use, and disclosure of information as described in this Privacy Policy.',
  ],
  sections: [
    {
      heading: '1. Information We Collect',
      blocks: [
        {
          sub: 'a) Information you provide directly',
          paragraphs: [
            'You may provide information when you purchase tickets, create an account, contact support, participate in surveys or events, or submit content through MOSIDA experiences (e.g., photos, text prompts, voice input).',
          ],
        },
        {
          sub: 'b) Information collected automatically',
          paragraphs: [
            'When you use the Service, we may automatically collect activity data, device and network data, approximate location, email engagement data, log and security data, and information via cookies and similar technologies.',
          ],
        },
        {
          sub: 'c) Onsite interactive exhibit data',
          paragraphs: [
            'Depending on the installation, we may collect photos or video, audio, motion/gesture interaction data, experience metadata, and AI-generated outputs. MOSIDA does not require sensitive information to enjoy the museum; optional features can be declined.',
          ],
        },
      ],
    },
    {
      heading: '2. How We Use Your Information',
      blocks: [
        {
          paragraphs: [
            'We use information to operate and improve the Service, process tickets and transactions, deliver exhibits and AI art outputs, provide support, secure systems, send administrative and marketing communications (opt out anytime), run surveys/promotions, create anonymized analytics, and comply with legal obligations.',
          ],
        },
      ],
    },
    {
      heading: '3. Cookies and Similar Technologies',
      blocks: [
        {
          paragraphs: [
            'We may use cookies, pixels, tags, and similar tools to remember preferences, understand usage, measure campaigns, detect fraud, and improve the experience. Most browsers allow you to control cookies; blocking them may limit functionality.',
          ],
        },
      ],
    },
    {
      heading: '4. How We Share Information',
      blocks: [
        {
          paragraphs: [
            'We may share information with service providers, business partners and affiliates, advertising partners (if used), for legal/safety/enforcement purposes, in business transfers, and as aggregated or anonymized data that does not reasonably identify you.',
          ],
        },
      ],
    },
    {
      heading: '5. Controlling Your Personal Data',
      blocks: [
        {
          paragraphs: [
            'Depending on where you live, you may have rights to access, correct, delete, or object to certain processing. You can unsubscribe from marketing emails, control cookies in your browser, and request access or deletion by contacting us.',
          ],
        },
      ],
    },
    {
      heading: '6. Data Retention',
      blocks: [
        {
          paragraphs: [
            'We retain personal data only as long as reasonably necessary for ticketing, support, security, legal compliance, and dispute resolution. After a deletion request, some information may be retained where required by law or for legitimate business purposes.',
          ],
        },
      ],
    },
    {
      heading: '7. Children',
      blocks: [
        {
          paragraphs: [
            'The Service is not directed to children under 16, and we do not knowingly collect personal data from children under 16 without parental consent.',
          ],
        },
      ],
    },
    {
      heading: '8. Security',
      blocks: [
        {
          paragraphs: [
            'We use commercially reasonable safeguards to protect information. No method of transmission or storage is 100% secure.',
          ],
        },
      ],
    },
    {
      heading: '9. International Transfers',
      blocks: [
        {
          paragraphs: [
            'MOSIDA is based in the United States. Your data may be processed and stored in the U.S. and other countries where our service providers operate.',
          ],
        },
      ],
    },
    {
      heading: '10. Changes & Contact',
      blocks: [
        {
          paragraphs: [
            'We may update this Privacy Policy periodically. Continued use of the Service after changes means you accept the revised policy.',
            'Questions or requests: MOSIDA, Attn: Privacy / Legal, Miami, FL (Wynwood), Email: info@mosida.com',
          ],
        },
      ],
    },
  ],
};
